import {
   createEntityAdapter,
   createSelector,
   createSlice,
   PayloadAction,
} from "@reduxjs/toolkit"
import { handleInvalidVitalValue } from "./utils"
import { realtimeFeedReceived, realtimeFeedsReceived } from "./websocketSlice"
import { ProducerType, type WsFeedPayload } from "@/hooks/useRealtimeConnection"
import type { RootState } from "@/store"
import type { Alert } from "@/types/alert"
import type { Sensor } from "@/types/facility"
import { normalizeMacAddress } from "@/utils/index"
import { isEqual } from "@/utils/lodash"

export interface WearableRealtimeEntry {
   id: string
   anchorId: string | null
   inmate: {
      firstName?: string
      middleName?: string | null
      lastName?: string
      customIdentifier?: string
      id?: string
      externalId?: string
      bookingNumber?: string | null
   } | null
   vitals: {
      heartRate?: number | null
      oxygenLevel?: number | null
      battery?: number | null
   } | null
   lastPositionAt: number
   lastVitalAt: number | null
}

const adapter = createEntityAdapter<WearableRealtimeEntry>()

interface WearablesRealtimeState extends ReturnType<
   typeof adapter.getInitialState
> {
   currentLevelId: string | null
   disconnectTimestamps: Record<string, number>
}

export const initialState: WearablesRealtimeState = {
   ...adapter.getInitialState(),
   currentLevelId: null,
   disconnectTimestamps: {},
}

const isStaleAfterDisconnect = (
   state: WearablesRealtimeState,
   entityId: string,
   timestamp: number
): boolean => {
   const disconnectedAt = state.disconnectTimestamps[entityId]
   if (!disconnectedAt) return false

   if (timestamp > disconnectedAt) {
      delete state.disconnectTimestamps[entityId]
      return false
   }

   return true
}

const findWearableByInmateId = (
   state: WearablesRealtimeState,
   inmateId: string,
   entityId?: string
): WearableRealtimeEntry | undefined =>
   Object.values(state.entities).find(
      (wearable) =>
         wearable?.inmate?.id === inmateId && wearable.id !== entityId
   )

const wasWearableSwappedOut = (
   state: WearablesRealtimeState,
   entityId: string,
   inmateId: string | undefined
): boolean => !!inmateId && !!findWearableByInmateId(state, inmateId, entityId)

const handlePosition = (
   state: WearablesRealtimeState,
   entityId: string,
   payload: WsFeedPayload,
   timestamp: number
) => {
   const data = payload.data
   const anchorId = (data?.anchorId as string | undefined) ?? null
   const control = payload.control
   const existing = state.entities[entityId]
   const incomingInmateId = control?.inmate?.id

   if (isStaleAfterDisconnect(state, entityId, timestamp)) return

   if (wasWearableSwappedOut(state, entityId, incomingInmateId)) {
      adapter.removeOne(state, entityId)
      return
   }

   adapter.upsertOne(state, {
      id: entityId,
      anchorId,
      inmate: control?.inmate
         ? {
              id: control.inmate.id,
              externalId: control.inmate.external_id ?? undefined,
              customIdentifier: control.inmate.custom_identifier,
              firstName: control.inmate.first_name,
              middleName: control.inmate.middle_name,
              lastName: control.inmate.last_name,
              bookingNumber: control.inmate.booking_number,
           }
         : (existing?.inmate ?? null),
      vitals: existing?.vitals ?? null,
      lastPositionAt: timestamp,
      lastVitalAt: existing?.lastVitalAt ?? null,
   })
}

const handleVital = (
   state: WearablesRealtimeState,
   entityId: string,
   payload: WsFeedPayload,
   timestamp: number
) => {
   const data = payload.data
   const existing = state.entities[entityId]
   const control = payload.control
   const incomingInmateId = control?.inmate?.id

   if (isStaleAfterDisconnect(state, entityId, timestamp)) return

   if (wasWearableSwappedOut(state, entityId, incomingInmateId)) {
      adapter.removeOne(state, entityId)
      return
   }

   adapter.upsertOne(state, {
      id: entityId,
      anchorId: existing?.anchorId ?? null,
      inmate: control?.inmate
         ? {
              id: control.inmate.id,
              externalId: control.inmate.external_id ?? undefined,
              customIdentifier: control.inmate.custom_identifier,
              firstName: control.inmate.first_name,
              middleName: control.inmate.middle_name,
              lastName: control.inmate.last_name,
              bookingNumber: control.inmate.booking_number,
           }
         : (existing?.inmate ?? null),
      vitals: {
         heartRate: handleInvalidVitalValue(
            (data?.hr as number | null) ?? null
         ),
         oxygenLevel: handleInvalidVitalValue(
            (data?.spo2 as number | null) ?? null
         ),
         battery: (data?.battery as number | null) ?? null,
      },
      lastPositionAt: existing?.lastPositionAt ?? timestamp,
      lastVitalAt: timestamp,
   })
}

const applyRealtimeFeed = (
   state: WearablesRealtimeState,
   feed: { entityId: string; rawPayload: unknown; timestamp: number }
) => {
   const { entityId, rawPayload, timestamp } = feed
   const payload = rawPayload as WsFeedPayload
   if (payload.producer_type === ProducerType.WEARABLE_POSITIONS) {
      handlePosition(state, entityId, payload, timestamp)
   } else if (payload.producer_type === ProducerType.WEARABLE_VITALS) {
      handleVital(state, entityId, payload, timestamp)
   }
}

const wearableFeedCoalesceKey = (feed: {
   entityId: string
   rawPayload: unknown
}): string => {
   const payload = feed.rawPayload as WsFeedPayload
   return `${feed.entityId}::${payload.producer_type}`
}

const wearablesRealtimeSlice = createSlice({
   name: "wearablesRealtime",
   initialState,
   reducers: {
      resetWearables: () => initialState,
      switchLevel: (state, action: PayloadAction<string>) => {
         if (state.currentLevelId === action.payload) return
         state.currentLevelId = action.payload
         adapter.removeAll(state)
      },
      linkWearable: (
         state,
         action: PayloadAction<{
            wearableId: string
            inmate: WearableRealtimeEntry["inmate"]
         }>
      ) => {
         const { wearableId, inmate } = action.payload
         const inmateId = inmate?.id

         if (inmateId) {
            const previousWearableId = Object.entries(state.entities).find(
               ([id, entry]) =>
                  id !== wearableId && entry?.inmate?.id === inmateId
            )?.[0]

            if (previousWearableId) {
               adapter.removeOne(state, previousWearableId)
            }
         }

         const existing = state.entities[wearableId]
         adapter.upsertOne(state, {
            id: wearableId,
            anchorId: existing?.anchorId ?? null,
            inmate: inmate
               ? {
                    id: inmate.id,
                    externalId: inmate.externalId,
                    customIdentifier: inmate.customIdentifier,
                 }
               : (existing?.inmate ?? null),
            vitals: existing?.vitals ?? null,
            lastPositionAt: existing?.lastPositionAt ?? Date.now(),
            lastVitalAt: existing?.lastVitalAt ?? null,
         })
      },
      seedWearable: (
         state,
         action: PayloadAction<{
            id: string
            anchorId: string
            inmate: WearableRealtimeEntry["inmate"]
         }>
      ) => {
         const { id, anchorId, inmate } = action.payload
         adapter.addOne(state, {
            id,
            anchorId,
            inmate,
            vitals: null,
            lastPositionAt: Date.now(),
            lastVitalAt: null,
         })
      },
      removeWearable: (state, action: PayloadAction<string>) => {
         adapter.removeOne(state, action.payload)
         state.disconnectTimestamps[action.payload] = Date.now()
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(realtimeFeedReceived, (state, action) => {
            applyRealtimeFeed(state, action.payload)
         })
         .addCase(realtimeFeedsReceived, (state, action) => {
            const latestByKey = new Map<
               string,
               (typeof action.payload)[number]
            >()

            for (const feed of action.payload) {
               latestByKey.set(wearableFeedCoalesceKey(feed), feed)
            }

            for (const feed of latestByKey.values()) {
               applyRealtimeFeed(state, feed)
            }
         })
   },
})

export const {
   resetWearables,
   switchLevel,
   linkWearable,
   seedWearable,
   removeWearable,
} = wearablesRealtimeSlice.actions

const adapterSelectors = adapter.getSelectors(
   (state: RootState) => state.wearablesRealtimeReducer
)

export const selectWearableRealtimeEntities = adapterSelectors.selectEntities
export const selectWearableRealtimeIds = adapterSelectors.selectIds as (
   state: RootState
) => string[]
export const selectWearableRealtimeById = (
   state: RootState,
   wearableId: string
) => adapterSelectors.selectById(state, wearableId)

export interface WearableLocationData {
   idsByLocationId: Record<string, string[]>
   nameByWearableId: Record<string, string>
}

export interface WearableLocationInput {
   id: string
   name: string
   sensors?: Sensor[]
}

const selectLocationsArg = (
   _state: RootState,
   locations: WearableLocationInput[]
) => locations

export const selectWearableLocationData = createSelector(
   [
      selectWearableRealtimeEntities,
      selectWearableRealtimeIds,
      selectLocationsArg,
   ],
   (entities, ids, locations): WearableLocationData => {
      const anchorToLocationId = new Map<string, string>()
      const nameByLocationId = new Map<string, string>()

      locations.forEach((location) => {
         nameByLocationId.set(location.id, location.name)
         location.sensors?.forEach((sensor) => {
            if (sensor.macAddress) {
               anchorToLocationId.set(
                  normalizeMacAddress(sensor.macAddress),
                  location.id
               )
            }
         })
      })

      const idsByLocationId: Record<string, string[]> = {}
      locations.forEach((location) => {
         idsByLocationId[location.id] = []
      })

      const nameByWearableId: Record<string, string> = {}

      ids.forEach((id) => {
         const wearable = entities[id]
         if (!wearable) return

         const locationId = wearable.anchorId
            ? (anchorToLocationId.get(normalizeMacAddress(wearable.anchorId)) ??
              null)
            : null

         if (locationId) {
            idsByLocationId[locationId]?.push(id)
            nameByWearableId[id] = nameByLocationId.get(locationId) ?? ""
         } else {
            nameByWearableId[id] = ""
         }
      })

      return { idsByLocationId, nameByWearableId }
   },
   { memoizeOptions: { resultEqualityCheck: isEqual } }
)

const selectAlertsByInmateIdArg = (
   _state: RootState,
   alertsByInmateId: Record<string, Alert[]>
) => alertsByInmateId

export const selectWearableHasActiveAlertById = createSelector(
   [
      selectWearableRealtimeEntities,
      selectWearableRealtimeIds,
      selectAlertsByInmateIdArg,
   ],
   (entities, ids, alertsByInmateId): Set<string> => {
      const idsWithActiveAlert = new Set<string>()
      ids.forEach((id) => {
         const inmateId = entities[id]?.inmate?.id
         if (inmateId && alertsByInmateId[inmateId]?.length) {
            idsWithActiveAlert.add(id)
         }
      })
      return idsWithActiveAlert
   },
   { memoizeOptions: { resultEqualityCheck: isEqual } }
)

export default wearablesRealtimeSlice.reducer
